/**
 * The map: Mapbox (with a public token in the app config) or, without one, a simple drawn
 * map with the same pins, so the app works fully before any keys are set up.
 */
import { lazy, type ReactNode, Suspense, useEffect, useRef, useState } from "react";
import brand from "../../../../brand/brand.json";
import { appConfig } from "./config";
import type { LatLng } from "./types";

export type MapDriver = LatLng & { heading: number | null };

export type MapProps = {
  pickup?: LatLng | null;
  dropoff?: LatLng | null;
  stops?: LatLng[];
  driver?: MapDriver | null;
  /** What the car is, for screen readers: the customer's driver, or the driver themselves. */
  carLabel?: string;
  /** Where the phone is. */
  me?: LatLng | null;
  className?: string;
  /** Extra room at the bottom for a sheet over the map, in pixels. */
  bottomInset?: number;
  /** Short labels by the pins, e.g. the pickup's name, or by the car, e.g. "4 min". */
  labels?: { pickup?: ReactNode; dropoff?: ReactNode; driver?: ReactNode };
};

const MapboxMap = lazy(() => import("./mapbox-map"));

export function MapView(props: MapProps) {
  const token = appConfig().mapboxToken;
  if (token) {
    return (
      <Suspense fallback={<DrawnMap {...props} />}>
        <MapboxMap {...props} token={token} />
      </Suspense>
    );
  }
  return <DrawnMap {...props} />;
}

/** Points in the order they're drawn, for fitting the view. */
export function mapPoints(props: MapProps): LatLng[] {
  return [props.pickup, ...(props.stops ?? []), props.dropoff, props.driver, props.me].filter(
    (p): p is LatLng => Boolean(p),
  );
}

// ---------------------------------------------------------------------------
// The drawn map
// ---------------------------------------------------------------------------

const mercatorY = (lat: number) => {
  const rad = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
};

function useSize<T extends HTMLElement>(): [
  React.RefObject<T | null>,
  { width: number; height: number },
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 390, height: 300 });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

/** A number from 0 to 1 for a street or block, the same every time. */
function hash(a: number, b: number, c: number): number {
  let h = Math.imul(a, 374761393) + Math.imul(b, 668265263) + Math.imul(c, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

type Streets = { minor: string; major: string; parks: string };

type Axes = {
  toScreen: (u: number, v: number) => { x: number; y: number };
  fromScreen: (x: number, y: number) => { u: number; v: number };
};

/**
 * Made-up streets for the area on screen: two sets of roads at right angles, a bit off
 * north, with some missing (so it reads as blocks, not a grid), every fourth a main road,
 * and the odd park. They're fixed to the map, not the screen, so the same place gets the
 * same streets. Not real roads: the real map is Mapbox, when there's a token.
 */
function streets(
  { toScreen, fromScreen }: Axes,
  diagonal: Axes,
  width: number,
  height: number,
  pixelsPerDegree: number,
): Streets {
  // A spacing that's 40 to 80 pixels on screen, from ~100 m streets doubled as needed.
  const level = Math.round(Math.log2(56 / (0.0011 * pixelsPerDegree)));
  const step = 0.0011 * 2 ** level;
  const corners = [
    fromScreen(0, 0),
    fromScreen(width, 0),
    fromScreen(0, height),
    fromScreen(width, height),
  ];
  const box = (axes: Axes, spacing: number) => {
    const c = [
      [0, 0],
      [width, 0],
      [0, height],
      [width, height],
    ].map(([x, y]) => axes.fromScreen(x!, y!));
    return {
      u0: Math.floor(Math.min(...c.map((p) => p.u)) / spacing) - 1,
      u1: Math.ceil(Math.max(...c.map((p) => p.u)) / spacing) + 1,
      v0: Math.min(...c.map((p) => p.v)) - spacing,
      v1: Math.max(...c.map((p) => p.v)) + spacing,
    };
  };
  const u0 = Math.floor(Math.min(...corners.map((c) => c.u)) / step) - 1;
  const u1 = Math.ceil(Math.max(...corners.map((c) => c.u)) / step) + 1;
  const v0 = Math.floor(Math.min(...corners.map((c) => c.v)) / step) - 1;
  const v1 = Math.ceil(Math.max(...corners.map((c) => c.v)) / step) + 1;
  if ((u1 - u0) * (v1 - v0) > 4000) return { minor: "", major: "", parks: "" };
  const seg = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    `M${a.x.toFixed(1)},${a.y.toFixed(1)}L${b.x.toFixed(1)},${b.y.toFixed(1)}`;
  let minor = "";
  let major = "";
  let parks = "";
  for (let i = u0; i <= u1; i++) {
    for (let j = v0; j <= v1; j++) {
      // Along u = i (a road running in v), from block j to j + 1.
      const mainU = i % 5 === 0;
      if (mainU || hash(i, j, level * 2) > 0.3) {
        const d = seg(toScreen(i * step, j * step), toScreen(i * step, (j + 1) * step));
        if (mainU) major += d;
        else minor += d;
      }
      // Along v = j (a road running in u), from block i to i + 1.
      const mainV = j % 5 === 0;
      if (mainV || hash(j, i, level * 2 + 1) > 0.34) {
        const d = seg(toScreen(i * step, j * step), toScreen((i + 1) * step, j * step));
        if (mainV) major += d;
        else minor += d;
      }
      if (hash(i, j, level * 7 + 3) < 0.05) {
        const pad = step * 0.16;
        const a = toScreen(i * step + pad, j * step + pad);
        const b = toScreen((i + 1) * step - pad, j * step + pad);
        const c = toScreen((i + 1) * step - pad, (j + 1) * step - pad);
        const e = toScreen(i * step + pad, (j + 1) * step - pad);
        parks += `M${a.x},${a.y}L${b.x},${b.y}L${c.x},${c.y}L${e.x},${e.y}Z`;
      }
    }
  }
  // A few main roads cutting across at an angle, so it isn't all right angles.
  const wide = step * 7;
  const d = box(diagonal, wide);
  for (let i = d.u0; i <= d.u1; i++) {
    if (hash(i, level, 11) < 0.45) continue;
    const a = diagonal.toScreen(i * wide, d.v0);
    const b = diagonal.toScreen(i * wide, d.v1);
    major += seg(a, b);
  }
  return { minor, major, parks };
}

/** The streets' tilt from north. */
const TILT = (14 * Math.PI) / 180;

function DrawnMap(props: MapProps) {
  const [ref, { width, height }] = useSize<HTMLDivElement>();
  const inset = props.bottomInset ?? 0;
  const points = mapPoints(props);
  const centre = brand.map.centre;
  // The area to show: every point, with some room around them.
  const xs = (points.length ? points : [centre]).map((p) => p.lng);
  const ys = (points.length ? points : [centre]).map((p) => mercatorY(p.lat));
  let minX = Math.min(...xs);
  let maxX = Math.max(...xs);
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  // At least a few streets across, so a single pin isn't infinitely zoomed in.
  const minSpan = 0.012;
  const spanX = Math.max(maxX - minX, minSpan);
  const spanY = Math.max(maxY - minY, (minSpan * Math.PI) / 180);
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  minX = midX - spanX / 2;
  maxX = midX + spanX / 2;
  minY = midY - spanY / 2;
  maxY = midY + spanY / 2;
  const padX = 64;
  // Room for the buttons over the top of the map, and for labels over the pins.
  const padTop = props.labels ? 132 : 96;
  const padBottom = 56;
  const usableW = Math.max(width - padX * 2, 50);
  const usableH = Math.max(height - inset - padTop - padBottom, 50);
  // Degrees of longitude and mercator units differ by π/180.
  const scale = Math.min(usableW / (maxX - minX), usableH / (((maxY - minY) * 180) / Math.PI));
  const centreY = padTop + usableH / 2;
  const project = (p: LatLng) => ({
    x: width / 2 + (p.lng - midX) * scale,
    y: centreY - ((mercatorY(p.lat) - midY) * 180 * scale) / Math.PI,
  });
  // The streets' own axes (u, v), in degrees, tilted from the map's.
  const midDeg = (midY * 180) / Math.PI;
  const axes = (angle: number): Axes => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      toScreen: (u, v) => {
        const lng = u * cos - v * sin;
        const deg = u * sin + v * cos;
        return { x: width / 2 + (lng - midX) * scale, y: centreY - (deg - midDeg) * scale };
      },
      fromScreen: (x, y) => {
        const lng = (x - width / 2) / scale + midX;
        const deg = (centreY - y) / scale + midDeg;
        return { u: lng * cos + deg * sin, v: -lng * sin + deg * cos };
      },
    };
  };
  const roads = streets(axes(TILT), axes(TILT + 0.68), width, height, scale);

  const route = [props.pickup, ...(props.stops ?? []), props.dropoff].filter((p): p is LatLng =>
    Boolean(p),
  );
  const path = route
    .map(project)
    .map((p, i, all) => {
      if (i === 0) return `M${p.x},${p.y}`;
      // A gentle curve between stops reads as a journey rather than a ruler line.
      const prev = all[i - 1]!;
      const mx = (prev.x + p.x) / 2 + (p.y - prev.y) * 0.15;
      const my = (prev.y + p.y) / 2 - (p.x - prev.x) * 0.15;
      return `Q${mx},${my} ${p.x},${p.y}`;
    })
    .join(" ");

  const driver = props.driver ? project(props.driver) : null;
  const labels = props.labels ?? {};
  const label = (at: LatLng | null | undefined, content: ReactNode, key: string) => {
    if (!at || !content) return null;
    const { x, y } = project(at);
    // Leans into the map, so it isn't cut off at the sides.
    const left = x < width / 2;
    return (
      <div
        key={key}
        className={`map-label map-label-${key}`}
        style={{
          transform: `translate(${left ? x - 22 : x + 22}px, ${y - 22}px) translate(${left ? "0" : "-100%"}, -100%)`,
        }}
      >
        {content}
      </div>
    );
  };
  return (
    <div ref={ref} className={`map map-drawn ${props.className ?? ""}`} aria-label="Map" role="img">
      <svg width={width} height={height} aria-hidden="true">
        <rect width={width} height={height} fill="var(--map-bg)" />
        <path d={roads.parks} fill="var(--map-park)" />
        <g fill="none" strokeLinecap="round">
          <path d={roads.minor} stroke="var(--map-road-edge)" strokeWidth="4.5" />
          <path d={roads.major} stroke="var(--map-road-edge)" strokeWidth="8.5" />
          <path d={roads.minor} stroke="var(--map-road)" strokeWidth="2.6" />
          <path d={roads.major} stroke="var(--map-road-major)" strokeWidth="6" />
        </g>
        {path && route.length > 1 && (
          <>
            <path
              d={path}
              fill="none"
              stroke="var(--map-route)"
              strokeWidth="14"
              strokeLinecap="round"
              opacity="0.16"
            />
            <path
              d={path}
              fill="none"
              stroke="var(--map-route-casing)"
              strokeWidth="9"
              strokeLinecap="round"
            />
            <path
              d={path}
              fill="none"
              stroke="var(--map-route)"
              strokeWidth="5"
              strokeLinecap="round"
            />
          </>
        )}
        {props.me && <MeDot {...project(props.me)} />}
        {props.pickup && <Pin {...project(props.pickup)} kind="pickup" />}
        {(props.stops ?? []).map((stop) => (
          <Pin key={`${stop.lat},${stop.lng}`} {...project(stop)} kind="stop" />
        ))}
        {props.dropoff && <Pin {...project(props.dropoff)} kind="dropoff" />}
      </svg>
      {label(props.pickup, labels.pickup, "pickup")}
      {label(props.dropoff, labels.dropoff, "dropoff")}
      {driver && (
        <div
          className="map-car"
          style={{ transform: `translate(${driver.x}px, ${driver.y}px)` }}
          role="img"
          aria-label={props.carLabel ?? "Your driver"}
        >
          <CarMarker heading={props.driver?.heading ?? null} />
        </div>
      )}
      {label(props.driver, labels.driver, "driver")}
    </div>
  );
}

function Pin({ x, y, kind }: { x: number; y: number; kind: "pickup" | "dropoff" | "stop" }) {
  if (kind === "stop") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <circle r="8" fill="var(--surface)" />
        <circle r="5" fill="var(--muted)" />
      </g>
    );
  }
  return (
    <g transform={`translate(${x} ${y})`}>
      {kind === "pickup" ? (
        <>
          <circle r="24" fill="var(--primary)" opacity="0.16" />
          <circle r="13" fill="var(--primary)" />
          <circle r="5.5" fill="var(--bg)" />
        </>
      ) : (
        <>
          <rect x="-13" y="-13" width="26" height="26" rx="6" fill="var(--surface)" />
          <rect x="-10" y="-10" width="20" height="20" rx="4.5" fill="var(--text)" />
          <rect x="-3.5" y="-3.5" width="7" height="7" rx="1.5" fill="var(--surface)" />
        </>
      )}
    </g>
  );
}

function MeDot({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r="26" fill="var(--me)" opacity="0.18" className="pulse" />
      <circle r="11" fill="#fff" />
      <circle r="7.5" fill="var(--me)" />
    </g>
  );
}

/** The car from above, pointing the way it's going, in the firm's colour. */
export function CarMarker({ heading }: { heading: number | null }): ReactNode {
  return (
    <svg width="56" height="56" viewBox="-28 -28 56 56" className="car-marker" aria-hidden="true">
      <circle r="26" fill="var(--primary)" opacity="0.14" />
      <g transform={`rotate(${heading ?? 0})`}>
        <ellipse cx="0" cy="2" rx="12" ry="20" fill="#000" opacity="0.35" />
        <path
          d="M-10 -12c0-5.5 3.6-9 10-9s10 3.5 10 9v25c0 4.4-3.6 8-8 8h-4c-4.4 0-8-3.6-8-8z"
          fill="var(--primary)"
          stroke="#fff"
          strokeWidth="2"
        />
        <path
          d="M-7.6 -9.5c1.6-2.6 4.4-3.8 7.6-3.8s6 1.2 7.6 3.8l-1.8 5.3h-11.6z"
          fill="#0b0d12"
          opacity="0.82"
        />
        <path
          d="M-6.4 9.2h12.8l-1.2 4.4c-.3 1-1.2 1.6-2.2 1.6h-6c-1 0-1.9-.6-2.2-1.6z"
          fill="#0b0d12"
          opacity="0.7"
        />
        <rect x="-6.6" y="-3.2" width="13.2" height="11" rx="2.6" fill="#fff" opacity="0.16" />
        <path
          d="M-7.5 -18.6l2.4-1M7.5 -18.6l-2.4-1"
          stroke="#fff"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
